import React, { useContext, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import PrintIcon from "@mui/icons-material/Print";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import * as XLSX from "xlsx";
import { ContextSession } from "../../services/ContextSession";
import logoInjupemp from "../../img/LogoInjupemp2026.png";
import {
  obtenerReporteProduccionEnrolamiento,
} from "./enrolamientoApi";
import {
  formatIsoDateToEs,
  getCurrentMonthDateRange,
  isSinOficinaReporte,
  openHtmlPrintWindow,
  resolveReporteOfficeName,
  safeText,
  validateReportDateRange,
} from "./reporteriaUtils";

const buildPrintHtml = (payload) => {
  const filtros = payload?.filtros || {};
  const grupos = (Array.isArray(payload?.grupos) ? payload.grupos : []).filter(
    (group) =>
      !isSinOficinaReporte({
        oficinaNombre: group?.OficinaNombre,
        oficinaOrigen: group?.OficinaOrigen,
        oficinaId: group?.OficinaId,
        oficinaKey: group?.OficinaKey,
      }),
  );
  const gruposHtml = grupos
    .map((group) => {
      const rows = Array.isArray(group?.Registros) ? group.Registros : [];
      const rowHtml = rows
        .map(
          (row) => `
            <tr>
              <td>${safeText(row?.FechaRegistro)}</td>
              <td>${safeText(row?.DocumentoNumero)}</td>
              <td>${safeText(row?.NombreCompleto)}</td>
              <td>${safeText(row?.FechaNacimiento)}</td>
              <td>${safeText(row?.Sexo)}</td>
            </tr>
          `,
        )
        .join("");
      const oficinaNombre = resolveReporteOfficeName({
        oficinaNombre: group?.OficinaNombre,
        oficinaOrigen: group?.OficinaOrigen,
        oficinaId: group?.OficinaId,
        oficinaKey: group?.OficinaKey,
      });
      return `
        <section class="group">
          <p class="office"><b>Oficina:</b> ${safeText(oficinaNombre)} (${safeText(group?.OficinaOrigen)})</p>
          <table>
            <thead>
              <tr>
                <th>Fecha Registro</th>
                <th>Identificación</th>
                <th>Nombre</th>
                <th>Fecha Nacimiento</th>
                <th>Sexo</th>
              </tr>
            </thead>
            <tbody>
              ${rowHtml}
            </tbody>
          </table>
        </section>
      `;
    })
    .join("");

  return `<!doctype html>
<html>
  <head>
    <meta charset="utf-8" />
    <title>Reporte de Producción - Enrolamiento</title>
    <style>
      @page { size: auto; margin: 12mm; }
      body { font-family: Arial, sans-serif; color: #111; margin: 20px; font-size: 12px; }
      .actions { text-align: right; margin-bottom: 10px; }
      .btn { border: 0; border-radius: 4px; background: #19498c; color: #fff; padding: 8px 14px; cursor: pointer; }
      .header { display: flex; align-items: center; gap: 18px; margin-bottom: 6px; }
      .logo { height: 60px; width: auto; }
      .title { font-size: 22px; font-weight: 700; margin: 0; }
      .date-row { display: flex; gap: 40px; margin-bottom: 12px; font-size: 18px; }
      .office { margin: 6px 0; font-size: 15px; }
      table { width: 100%; border-collapse: collapse; margin-bottom: 12px; }
      th, td { border: 1px solid #222; padding: 4px 6px; font-size: 12px; text-align: left; }
      th { background: #f1f4f9; }
      .group { margin-bottom: 12px; break-inside: auto; page-break-inside: auto; }
      thead { display: table-header-group; }
      tfoot { display: table-footer-group; }
      tr { break-inside: avoid; page-break-inside: avoid; }
      @media print {
        body { margin: 0; }
        .actions { display: none; }
      }
    </style>
  </head>
  <body>
    <div class="actions">
      <button class="btn" onclick="window.print()">Imprimir</button>
    </div>
    <div class="header">
      <img class="logo" src="${logoInjupemp}" alt="INJUPEMP" />
      <h1 class="title">Reporte de Producción</h1>
    </div>
    <div class="date-row">
      <div><b>Fecha Inicial:</b> ${formatIsoDateToEs(filtros?.fecha_inicio)}</div>
      <div><b>Fecha Final:</b> ${formatIsoDateToEs(filtros?.fecha_fin)}</div>
    </div>
    ${gruposHtml}
  </body>
</html>`;
};

const buildExcelRows = (payload) => {
  const grupos = (Array.isArray(payload?.grupos) ? payload.grupos : []).filter(
    (group) =>
      !isSinOficinaReporte({
        oficinaNombre: group?.OficinaNombre,
        oficinaOrigen: group?.OficinaOrigen,
        oficinaId: group?.OficinaId,
        oficinaKey: group?.OficinaKey,
      }),
  );
  return grupos.flatMap((group) => {
    const rows = Array.isArray(group?.Registros) ? group.Registros : [];
    const oficinaNombre = resolveReporteOfficeName({
      oficinaNombre: group?.OficinaNombre,
      oficinaOrigen: group?.OficinaOrigen,
      oficinaId: group?.OficinaId,
      oficinaKey: group?.OficinaKey,
    });
    return rows.map((row) => ({
      Oficina: safeText(oficinaNombre),
      Origen: safeText(group?.OficinaOrigen),
      "Fecha Registro": safeText(row?.FechaRegistro),
      Identificacion: safeText(row?.DocumentoNumero),
      Nombre: safeText(row?.NombreCompleto),
      "Fecha Nacimiento": safeText(row?.FechaNacimiento),
      Sexo: safeText(row?.Sexo),
    }));
  });
};

const toSafeFileDate = (value) => {
  const raw = safeText(value) || "sin-fecha";
  return raw.replace(/[^\dA-Za-z-]/g, "-");
};

export default function ReporteProduccionEnrolamiento() {
  const sessi = useContext(ContextSession);
  const authToken = sessi?.sessionData?.token || "";

  const currentMonthRange = getCurrentMonthDateRange();
  const [fechaInicio, setFechaInicio] = useState(currentMonthRange.fechaInicio);
  const [fechaFin, setFechaFin] = useState(currentMonthRange.fechaFin);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [reporte, setReporte] = useState(null);

  const handleConsultar = async () => {
    const validationError = validateReportDateRange(fechaInicio, fechaFin);
    if (validationError) {
      setError(validationError);
      setReporte(null);
      return;
    }

    try {
      setLoading(true);
      setError("");
      const data = await obtenerReporteProduccionEnrolamiento(authToken, {
        fechaInicio,
        fechaFin,
      });
      setReporte(data || null);
    } catch (err) {
      setError(err?.message || "No se pudo generar el reporte.");
      setReporte(null);
    } finally {
      setLoading(false);
    }
  };

  const handleImprimir = () => {
    if (!reporte) return;
    openHtmlPrintWindow(buildPrintHtml(reporte));
  };

  const handleExportarExcel = () => {
    if (!reporte) return;
    const rows = buildExcelRows(reporte);
    if (!rows.length) return;

    const workbook = XLSX.utils.book_new();
    const worksheet = XLSX.utils.json_to_sheet(rows);
    worksheet["!cols"] = [
      { wch: 28 },
      { wch: 13 },
      { wch: 18 },
      { wch: 18 },
      { wch: 45 },
      { wch: 18 },
      { wch: 8 },
    ];
    XLSX.utils.book_append_sheet(workbook, worksheet, "Produccion");

    const fechaInicio = toSafeFileDate(reporte?.filtros?.fecha_inicio);
    const fechaFin = toSafeFileDate(reporte?.filtros?.fecha_fin);
    XLSX.writeFile(workbook, `reporte_produccion_${fechaInicio}_a_${fechaFin}.xlsx`);
  };

  const grupos = (Array.isArray(reporte?.grupos) ? reporte.grupos : []).filter(
    (group) =>
      !isSinOficinaReporte({
        oficinaNombre: group?.OficinaNombre,
        oficinaOrigen: group?.OficinaOrigen,
        oficinaId: group?.OficinaId,
        oficinaKey: group?.OficinaKey,
      }),
  );
  const totalRegistrosVisibles = grupos.reduce(
    (acc, group) => acc + Number(group?.Total || 0),
    0,
  );

  return (
    <Box sx={{ p: 3 }}>
      <Paper sx={{ p: 2.5, borderRadius: 3, border: "1px solid #dce7f7" }}>
        <Typography variant="h6" sx={{ fontWeight: 700, color: "#19498c", mb: 2 }}>
          Reporte de Producción
        </Typography>

        <Stack direction={{ xs: "column", md: "row" }} spacing={2} sx={{ mb: 2 }}>
          <TextField
            label="Fecha inicio"
            type="date"
            size="small"
            value={fechaInicio}
            onChange={(event) => setFechaInicio(event.target.value)}
            InputLabelProps={{ shrink: true }}
            inputProps={{ max: fechaFin }}
          />
          <TextField
            label="Fecha final"
            type="date"
            size="small"
            value={fechaFin}
            onChange={(event) => setFechaFin(event.target.value)}
            InputLabelProps={{ shrink: true }}
            inputProps={{ min: fechaInicio }}
          />
          <Button
            variant="contained"
            startIcon={<SearchIcon />}
            onClick={handleConsultar}
            disabled={loading}
            sx={{ textTransform: "none", fontWeight: 700, backgroundColor: "#19498c" }}
          >
            {loading ? "Consultando..." : "Consultar"}
          </Button>
          <Button
            variant="outlined"
            startIcon={<PrintIcon />}
            onClick={handleImprimir}
            disabled={!reporte}
            sx={{ textTransform: "none", fontWeight: 700 }}
          >
            Imprimir
          </Button>
          <Button
            variant="outlined"
            color="success"
            startIcon={<FileDownloadIcon />}
            onClick={handleExportarExcel}
            disabled={!reporte || !grupos.length}
            sx={{ textTransform: "none", fontWeight: 700 }}
          >
            Excel
          </Button>
        </Stack>

        {error ? <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert> : null}

        {reporte ? (
          <>
            <Typography sx={{ color: "#4f5f77", fontSize: 13, mb: 2 }}>
              Período: {formatIsoDateToEs(reporte?.filtros?.fecha_inicio)} a {formatIsoDateToEs(reporte?.filtros?.fecha_fin)}.{" "}
              Total registros: <b>{totalRegistrosVisibles}</b>
            </Typography>

            {grupos.map((group) => {
              const registros = Array.isArray(group?.Registros) ? group.Registros : [];
              const oficinaNombre = resolveReporteOfficeName({
                oficinaNombre: group?.OficinaNombre,
                oficinaOrigen: group?.OficinaOrigen,
                oficinaId: group?.OficinaId,
                oficinaKey: group?.OficinaKey,
              });
              return (
                <Paper key={group?.OficinaKey} variant="outlined" sx={{ p: 1.5, mb: 2 }}>
                  <Typography sx={{ fontWeight: 700, color: "#1a2f4d", mb: 0.8 }}>
                    Oficina: {oficinaNombre} ({group?.OficinaOrigen}) - Total: {Number(group?.Total || 0)}
                  </Typography>
                  <Box sx={{ overflowX: "auto" }}>
                    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                      <thead>
                        <tr>
                          <th style={{ border: "1px solid #c5cedd", padding: 6, textAlign: "left" }}>Fecha Registro</th>
                          <th style={{ border: "1px solid #c5cedd", padding: 6, textAlign: "left" }}>Identificación</th>
                          <th style={{ border: "1px solid #c5cedd", padding: 6, textAlign: "left" }}>Nombre</th>
                          <th style={{ border: "1px solid #c5cedd", padding: 6, textAlign: "left" }}>Fecha Nacimiento</th>
                          <th style={{ border: "1px solid #c5cedd", padding: 6, textAlign: "left" }}>Sexo</th>
                        </tr>
                      </thead>
                      <tbody>
                        {registros.map((row, index) => (
                          <tr key={`${row?.PersonaId || row?.DocumentoNumero || ""}-${index}`}>
                            <td style={{ border: "1px solid #e0e5ef", padding: 6 }}>{row?.FechaRegistro || ""}</td>
                            <td style={{ border: "1px solid #e0e5ef", padding: 6 }}>{row?.DocumentoNumero || ""}</td>
                            <td style={{ border: "1px solid #e0e5ef", padding: 6 }}>{row?.NombreCompleto || ""}</td>
                            <td style={{ border: "1px solid #e0e5ef", padding: 6 }}>{row?.FechaNacimiento || ""}</td>
                            <td style={{ border: "1px solid #e0e5ef", padding: 6 }}>{row?.Sexo || ""}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </Box>
                </Paper>
              );
            })}
          </>
        ) : (
          <Typography sx={{ color: "#5a6a80" }}>
            Seleccione la fecha inicial y la fecha final para generar el reporte.
          </Typography>
        )}
      </Paper>
    </Box>
  );
}
